/**
 * Shared pieces for the Reports pages (DESIGN-reports.md, step 0b, 23/09/2026).
 *
 * ⚠️ WARM HIVE COLOURS ON THE REPORT PAGES ONLY (decision P). The `hive-*` Tailwind tokens
 * come from design_handoff_holidayhive/README.md; the sidebar and the rest of the app stay
 * as they are until the redesign. Every colour has a `dark:` partner so dark mode still
 * reads — the warm cream is a light-mode look.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Printer, Menu, CalendarRange, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import Sidebar from '../Sidebar';
import BrandedToaster from '../BrandedToaster';

export const HEX = 'polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)';

/** 12, 12.5, 0.57 — never "12.0", UK separators for thousands. */
export const fmtDays = (x) =>
  Number(x || 0).toLocaleString('en-GB', { maximumFractionDigits: 2 });

/** The page frame: today's sidebar, and a warm main area. The sidebar is left out of print. */
export const ReportFrame = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-background">
      <BrandedToaster />
      <div className="print:hidden">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      </div>
      <main className="flex-1 min-w-0 overflow-auto bg-hive-cream dark:bg-background font-nunito text-hive-navy dark:text-foreground">
        <div className="lg:hidden px-4 pt-4 print:hidden">
          <button onClick={() => setSidebarOpen(true)} aria-label="Open menu"><Menu className="w-6 h-6" /></button>
        </div>
        <div className="px-4 md:px-8 py-6 max-w-[1320px]">{children}</div>
      </main>
    </div>
  );
};

export const Card = ({ className = '', children, ...rest }) => (
  <div
    className={`rounded-[20px] border border-hive-line bg-white dark:bg-card dark:border-border p-5 ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export const Pill = ({ className = '', children, ...rest }) => (
  <span
    className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-hive-line bg-hive-tile dark:bg-card dark:border-border px-3.5 py-1.5 text-[13px] font-bold ${className}`}
    {...rest}
  >
    {children}
  </span>
);

export const Chip = ({ tone = 'grey', children }) => {
  const tones = {
    grey: 'bg-[#EEE8DE] text-hive-muted dark:bg-muted dark:text-muted-foreground',
    pending: 'bg-hive-pending-bg text-hive-pending-fg',
    danger: 'bg-hive-danger-bg text-hive-danger-fg',
    success: 'bg-hive-success-bg text-hive-success-fg',
    // Booked ahead on What we owe (H5, 20/10/2026) — the blue of the mock-up.
    info: 'bg-[#E6EEF8] text-[#1D4E89] dark:bg-muted dark:text-foreground',
  };
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11.5px] font-extrabold ${tones[tone] || tones.grey}`}>
      {children}
    </span>
  );
};

/** A select that looks like the design's filter pills. */
export const PillSelect = ({ value, onChange, children, label }) => (
  <select
    aria-label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="rounded-full border border-hive-line bg-hive-tile dark:bg-card dark:border-border px-3.5 py-1.5 text-[13px] font-bold text-hive-navy dark:text-foreground cursor-pointer"
  >
    {children}
  </select>
);

/**
 * The period control (decision H): the leave year with Previous / Next, or chosen dates.
 * `period` is what the server answered with; `onYear(year)` and `onDates(start, end)` ask
 * for a new one. `allowDates` is off on the home page, which is always a leave year.
 */
export const PeriodPicker = ({ period, onYear, onDates, allowDates = true, backLabel = 'Back to leave year' }) => {
  const [choosing, setChoosing] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const custom = period?.kind === 'custom';
  const year = period?.year;
  const ready = start && end && end >= start;

  const apply = () => {
    if (!ready) return;
    setChoosing(false);
    onDates(start, end);
  };

  // ⚠️ Rob, 24/09/2026: "I can not see Back to leave year". It used to sit INSIDE the date
  // pill as small orange text; it is now a button of its own, next to the dates.
  return (
    <div className="flex flex-wrap items-center gap-2 print:hidden">
      {custom ? (
        <>
          <Pill><CalendarRange className="w-4 h-4 text-hive-muted" />{period.label}</Pill>
          <button onClick={() => onYear(null)} data-testid="back-to-leave-year">
            <Pill className="hover:border-hive-orange"><ChevronLeft className="w-4 h-4" /> {backLabel}</Pill>
          </button>
        </>
      ) : period?.kind === 'rolling' ? (
        // Reports → Sickness: "Last 12 months" up to today (Rob's 4a) — no year arrows.
        <Pill><CalendarRange className="w-4 h-4 text-hive-muted" /><span title={period?.range}>{period.label}</span></Pill>
      ) : (
        <Pill>
          <button aria-label="Previous leave year" onClick={() => year && onYear(year - 1)} className="text-hive-muted hover:text-hive-navy">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span title={period?.range}>{period?.label || 'Leave year'}</span>
          <button aria-label="Next leave year" onClick={() => year && onYear(year + 1)} className="text-hive-muted hover:text-hive-navy">
            <ChevronRight className="w-4 h-4" />
          </button>
        </Pill>
      )}
      {allowDates && !choosing && (
        <button onClick={() => setChoosing(true)}>
          <Pill className="hover:border-hive-orange">{custom ? 'Change dates' : 'Choose dates'}</Pill>
        </button>
      )}
      {allowDates && choosing && (
        <Pill>
          <input type="date" aria-label="From" value={start} onChange={(e) => setStart(e.target.value)} className="bg-transparent" />
          <span className="text-hive-muted">to</span>
          <input type="date" aria-label="To" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-transparent" />
          <button
            className="rounded-full bg-hive-orange hover:bg-hive-orange-hover text-white px-3 py-0.5 font-extrabold disabled:opacity-40"
            disabled={!ready}
            onClick={apply}
          >
            Show
          </button>
          <button className="text-hive-muted hover:text-hive-navy" onClick={() => setChoosing(false)}>Cancel</button>
        </Pill>
      )}
    </div>
  );
};

/** Download ▾ with its choices; closes on a click anywhere else. */
export const DownloadMenu = ({ options, onPick, busy, label = 'Download', small = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className="relative print:hidden" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        className={small
          ? 'text-[13px] font-bold text-hive-muted hover:text-hive-navy dark:hover:text-foreground inline-flex items-center gap-1'
          : 'inline-flex items-center gap-2 rounded-full bg-hive-orange hover:bg-hive-orange-hover text-white px-4 py-1.5 text-[13px] font-bold shadow-[0_6px_16px_rgba(247,135,31,.3)] disabled:opacity-60'}
      >
        <Download className="w-4 h-4" /> {busy ? 'Preparing…' : label} ▾
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-56 rounded-xl border border-hive-line dark:border-border bg-white dark:bg-card shadow-lg py-1">
          {options.map((o) => (
            <button
              key={o.value}
              onClick={() => { setOpen(false); onPick(o.value); }}
              className="block w-full text-left px-4 py-2 text-sm hover:bg-hive-cream dark:hover:bg-muted"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const PrintButton = () => (
  <button onClick={() => window.print()} className="print:hidden">
    <Pill className="hover:border-hive-orange"><Printer className="w-4 h-4" /> Print</Pill>
  </button>
);

/** The "no access" and "went wrong" states every report page shares. */
export const NoAccess = () => (
  <Card className="max-w-xl mx-auto text-center py-10">
    <p className="font-poppins font-semibold text-lg">You don't have access to reports</p>
    <p className="text-sm text-hive-muted dark:text-muted-foreground mt-1">Ask an administrator if you need it.</p>
  </Card>
);

/** A small "i" that explains a figure — tap or click (Rob, 09/10/2026: "Working days left"
 *  needed one). A popover rather than a hover tooltip, so it works on a phone too. */
export const InfoTip = ({ label, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      {/* ⚠️ Orange and 16px, not grey and 14px: Rob couldn't find the grey one (12/10/2026). */}
      <button type="button" aria-label={label || 'What does this mean?'}
        className="inline-flex align-middle ml-0.5 text-hive-orange hover:text-hive-orange-hover print:hidden">
        <Info className="w-4 h-4" strokeWidth={2.5} />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-72 text-[12.5px] leading-snug font-nunito">{children}</PopoverContent>
  </Popover>
);

/**
 * A report table whose column headings stay put while you scroll (Rob, 12/10/2026: "you lose
 * the headers" on a long list). The table scrolls inside its card — sideways on a phone too —
 * and the outer table's headings stick to the top; tables nested in a drill-down row don't.
 * Printing shows the whole list.
 */
export const ScrollTable = ({ children, className = '' }) => (
  <div className={`overflow-auto max-h-[75vh] print:max-h-none print:overflow-visible
    [&>table>thead>tr>th]:sticky [&>table>thead>tr>th]:top-0 [&>table>thead>tr>th]:z-10
    [&>table>thead>tr>th]:bg-white dark:[&>table>thead>tr>th]:bg-card
    [&>table>thead>tr>th]:shadow-[inset_0_-1px_0_#EFE4D3] dark:[&>table>thead>tr>th]:shadow-none ${className}`}
       data-testid="scroll-table">
    {children}
  </div>
);

